import { Field, Input, Label } from '@headlessui/react';
import {
  useState,
  ChangeEvent,
  FocusEvent,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';
import { cn } from 'utils/cn';
import RequiredMark from 'components/common/RequiredMark';

interface Country {
  name: string;
  code: string;
  dial_code: string;
  flag: string;
}

interface CustomPhoneInputProps {
  label?: string;
  name: string;
  value: string;
  code: string;
  setCode: (code: string) => void;
  data: Country[];
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  error?: string | false;
  placeholder?: string;
  required?: boolean;
  info?: boolean;
  readOnly?: boolean;
  dis?: boolean;
  darkBg?: boolean;
  className?:
    | string
    | {
        label?: string;
        input?: string;
        field?: string;
      };
}

const CustomPhoneInput = ({
  label,
  name,
  value,
  code,
  setCode,
  data,
  onChange,
  onBlur,
  error,
  placeholder,
  required = false,
  info = false,
  readOnly = false,
  dis = false,
  darkBg = false,
  className,
}: CustomPhoneInputProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('');

  const containerRef = useRef<HTMLDivElement>(null);
  const lastSyncedCode = useRef<string>('');

  useEffect(() => {
    if (data.length > 0 && code !== lastSyncedCode.current) {
      const matchingCountry = data.find(
        (country) => country.dial_code === code,
      );
      if (matchingCountry) {
        setSelectedCountryCode(matchingCountry.code);
        lastSyncedCode.current = code;
      }
    }
  }, [code, data]);

  const selectedCountry = useMemo(() => {
    return data.find((country) => country.code === selectedCountryCode);
  }, [selectedCountryCode, data]);

  const filteredCountries = useMemo(() => {
    if (!searchTerm) return data;
    return data.filter(
      (country) =>
        country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.dial_code.includes(searchTerm),
    );
  }, [searchTerm, data]);

  const handleCountrySelect = (countryCode: string, dialCode: string) => {
    setSelectedCountryCode(countryCode);
    lastSyncedCode.current = dialCode;
    setCode(dialCode);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const rawVal = e.target.value;
    const cleaned = rawVal.replace(/\D/g, '').slice(0, 10);
    e.target.value = cleaned;
    onChange(e);
  };

  const handleInputFocus = () => setIsFocused(true);

  const handleInputBlur = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    if (onBlur) onBlur(e);
  };

  const handleDropdownToggle = () => {
    setIsOpen((prev) => !prev);
    setSearchTerm('');
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isAnyFocused = isFocused || isOpen;

  const classes =
    typeof className === 'string' ? { label: className } : (className ?? {});

  return (
    <Field>
      <div className="relative w-full" ref={containerRef}>
        {/* LABEL - outside the input container, matching FormInput style */}
        {label && (
          <label
            htmlFor={name}
            className={cn(
              'block text-[14px] font-normal text-neutral-medium mb-[11px] leading-[100%]',
              classes.label,
            )}
          >
            {label}
            {required && <RequiredMark />}
            {info && (
              <Info className="inline text-[#9CA3AF] w-3.5 h-3.5 ml-1" />
            )}
          </label>
        )}

        {/* INPUT CONTAINER - h-[46px], rounded-[8px], #1E2230 bg, #2A2F3D border */}
        <div
          className={cn(
            `flex items-center w-full h-[46px] rounded-[8px] border transition-colors duration-200 ${darkBg ? 'bg-primary-light' : 'bg-[#1E2230]'} ${
              error
                ? 'border-red-500'
                : isAnyFocused
                  ? 'border-[#6366f1]'
                  : 'border-[#2A2F3D]'
            }`,
            classes.input,
          )}
        >
          {/* COUNTRY SELECTOR */}
          <div
            className="flex items-center h-full px-3 cursor-pointer border-r border-[#2A2F3D] shrink-0"
            onClick={handleDropdownToggle}
            tabIndex={0}
            aria-expanded={isOpen}
            style={{ minWidth: '80px' }}
          >
            <div className="flex items-center gap-1.5 w-full">
              {selectedCountry ? (
                <>
                  <img
                    src={`https://flagcdn.com/w40/${selectedCountry.code.toLowerCase()}.png`}
                    alt={selectedCountry.code}
                    className="w-5 h-5 rounded-full object-cover shrink-0"
                  />
                  <span className="text-white text-[14px] font-medium">
                    {selectedCountry.code}
                  </span>
                </>
              ) : (
                <span className="text-[#374151] text-[14px]">Country</span>
              )}
              {isOpen ? (
                <ChevronUp className="h-4 w-4 text-[#9CA3AF] ml-auto shrink-0" />
              ) : (
                <ChevronDown className="h-4 w-4 text-[#9CA3AF] ml-auto shrink-0" />
              )}
            </div>
          </div>

          {/* PHONE NUMBER INPUT */}
          <Input
            type="tel"
            name={name}
            id={name}
            value={value}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            readOnly={readOnly}
            disabled={dis}
            placeholder={placeholder}
            className={cn(
              'flex-1 min-w-0 w-full h-full bg-transparent border-none focus:ring-0 outline-none px-4 text-white text-[16px] placeholder:text-[#374151] placeholder:font-normal',
              classes.field,
            )}
          />
        </div>

        {/* DROPDOWN */}
        {isOpen && (
          <div className="absolute z-50 mt-1 w-[300px] bg-[#1E2230] text-white shadow-lg rounded-[8px] border border-[#2A2F3D] max-h-64 overflow-y-auto">
            <div className="p-2">
              <input
                type="text"
                placeholder="Search code or country"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-2 bg-[#252a3a] border border-[#2A2F3D] rounded-[6px] text-white text-[14px] outline-none placeholder:text-[#9CA3AF]"
                autoFocus
              />
            </div>
            <ul className="p-1">
              {filteredCountries.length > 0 ? (
                filteredCountries.map((country) => (
                  <li
                    key={country.code}
                    className="px-3 py-2 hover:bg-[#252a3a] cursor-pointer flex justify-between text-sm items-center rounded-[4px]"
                    onClick={() =>
                      handleCountrySelect(country.code, country.dial_code)
                    }
                  >
                    <span className="flex items-center gap-2">
                      <img
                        src={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png`}
                        alt={country.code}
                        className="w-5 h-5 rounded-full object-cover"
                      />
                      <span>{country.name}</span>
                    </span>
                    <span className="text-[#9CA3AF]">{country.dial_code}</span>
                  </li>
                ))
              ) : (
                <li className="px-3 py-2 text-[#9CA3AF]">No countries found</li>
              )}
            </ul>
          </div>
        )}

        {/* ERROR */}
        {error && <div className="text-red-500 text-xs mt-1 px-1">{error}</div>}
      </div>
    </Field>
  );
};

export default CustomPhoneInput;
